import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconPlus, IconUsers } from "@tabler/icons-react";
import { Heading } from "../../../../components/common/typography";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { Select } from "../../../../components/forms/select";
import { SimpleTable } from "../../../../components/common/simple-table";
import { EmptyState } from "../../../../components/common/empty-state";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { Button } from "../../../../components/common/button";
import { AccentStatCard } from "../../../../components/common/accent-stat-card";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { usePartners } from "../../hooks/usePartners";
import { usePartnerStats } from "../../hooks/usePartnerStats";
import { buildPartnersColumns } from "../components/partners-simple-columns";
import { PartnersQueryDtoTypeEnum, PartnersQueryDtoSortOrderEnum } from "../../../../api";
import type { PartnersQueryDto } from "../../../../api";

type TypeFilter = "all" | "seller" | "buyer";

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "seller", label: "Sellers" },
  { value: "buyer", label: "Buyers" },
];

const SORT_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

const VALID_TYPES = new Set<TypeFilter>(TYPE_OPTIONS.map((o) => o.value));
const PAGE_SIZE = 10;

export function PartnersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const typeParam = searchParams.get("type") as TypeFilter | null;
  const type: TypeFilter = typeParam && VALID_TYPES.has(typeParam) ? typeParam : "all";
  const search = searchParams.get("search") ?? "";
  const sort = searchParams.get("sort") ?? "desc";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const setType = (next: TypeFilter) => {
    const p = new URLSearchParams(searchParams);
    if (next === "all") p.delete("type");
    else p.set("type", next);
    p.delete("page");
    setSearchParams(p);
  };

  const query: PartnersQueryDto = {
    search: search.trim() || undefined,
    type: type === "seller"
      ? PartnersQueryDtoTypeEnum.Supplier
      : type === "buyer"
        ? PartnersQueryDtoTypeEnum.Buyer
        : undefined,
    sortOrder: sort === "asc" ? PartnersQueryDtoSortOrderEnum.Asc : PartnersQueryDtoSortOrderEnum.Desc,
    page,
    pageSize: PAGE_SIZE,
  };

  const stats = usePartnerStats();
  const { data, isLoading } = usePartners(query);
  const partners = data?.data ?? [];
  const pageCount = data?.meta?.pageCount ?? 1;

  const columns = useMemo(
    () => buildPartnersColumns((id) => navigate(`${AppRoutes.PARTNERS}/${id}`)),
    [navigate],
  );

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-3">
        <Heading variant="h2" tone="dark">Partners</Heading>
        <Button
          variant="filledDark"
          size="md"
          leftIcon={<IconPlus size={16} />}
          onClick={() => navigate(AppRoutes.CREATE_PARTNER)}
        >
          Create Partner
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1 max-md:gap-3">
        <AccentStatCard accent="green" label="Total Partners" value={(stats.total ?? 0).toLocaleString()} />
        <AccentStatCard accent="cyan" label="Total Sellers" value={(stats.sellers ?? 0).toLocaleString()} />
        <AccentStatCard accent="amber" label="Total Buyers" value={(stats.buyers ?? 0).toLocaleString()} />
      </div>

      <div className="flex items-center gap-3 max-md:flex-col max-md:items-stretch">
        <SegmentedControl
          options={TYPE_OPTIONS}
          value={type}
          onChange={setType}
          aria-label="Filter partners by type"
          className="max-md:w-full max-md:justify-between"
        />

        <div className="flex-1">
          <SearchBar
            value={search}
            onChange={(e) => {
              const p = new URLSearchParams(searchParams);
              if (e.target.value.trim()) p.set("search", e.target.value);
              else p.delete("search");
              p.delete("page");
              setSearchParams(p);
            }}
            placeholder="Search"
            wrapperClassName=""
          />
        </div>

        <Select
          name="partners-sort"
          value={sort}
          onChange={(e) => {
            const p = new URLSearchParams(searchParams);
            if (e.target.value === "desc") p.delete("sort");
            else p.set("sort", e.target.value);
            p.delete("page");
            setSearchParams(p);
          }}
          options={SORT_OPTIONS}
          density="compact"
          fullWidth={false}
          className="min-w-[160px]"
        />
      </div>

      {isLoading ? (
        <ShimmerLoader message="Loading partners…" lines={6} />
      ) : partners.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite">
          <EmptyState
            icon={IconUsers}
            title="No partners yet"
            description="Create your first partner to get started."
          />
        </div>
      ) : (
        <SimpleTable
          columns={columns}
          data={partners}
          keyExtractor={(p) => String(p.id)}
          onRowClick={(p) => navigate(`${AppRoutes.PARTNERS}/${p.id}`)}
          footer={
            pageCount > 1 ? (
              <div className="flex justify-center">
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={(next) => {
                    const p = new URLSearchParams(searchParams);
                    p.set("page", String(next));
                    setSearchParams(p, { replace: true });
                  }}
                />
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
